import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import WizardShell from '../ui/wizard-shell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { relationshipOptionsFor } from '../../lib/subprofileRelationships';
import type { Kontaktweg } from '../../lib/api/stammdaten';
import KontaktwegeEditor from './KontaktwegeEditor';
interface Person {
  id: string;
  name: string;
  [key: string]: any;
}
interface SubProfileFormData {
  salutation?: 'Herr' | 'Frau' | 'Divers' | 'Firma';
  relationshipType?: string;
  firstName: string;
  lastName: string;
  name?: string; // optional; wird aus first+last gebildet, falls leer
  birthDate: string;
  communications: Kontaktweg[];
}
interface SubProfileFormProps {
  parent: Person | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: SubProfileFormData) => void;
  initialData?: Partial<SubProfileFormData> | null;
}
const SubProfileForm = ({
  parent,
  isOpen,
  onClose,
  onSave,
  initialData
}: SubProfileFormProps) => {
  const [formData, setFormData] = useState<SubProfileFormData>({
    salutation: undefined,
    relationshipType: '',
    firstName: '',
    lastName: '',
    name: '',
    birthDate: '',
    communications: [],
  });
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        salutation: (initialData.salutation as any) ?? undefined,
        relationshipType: initialData.relationshipType ?? '',
        firstName: initialData.firstName ?? '',
        lastName: initialData.lastName ?? '',
        name: initialData.name ?? '',
        birthDate: initialData.birthDate ?? '',
        communications: initialData.communications ? [...initialData.communications] : [],
      }));
    } else {
      setFormData({
        salutation: undefined,
        relationshipType: '',
        firstName: '',
        lastName: '',
        name: '',
        birthDate: '',
        communications: [],
      });
    }
  }, [initialData]);
  if (!parent) return null;
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Passt die Beziehung nicht mehr zur Anrede, wird sie geleert (T-0056).
      if (
        name === 'salutation' &&
        prev.relationshipType &&
        !relationshipOptionsFor(value).some((o) => o.value === prev.relationshipType)
      ) {
        next.relationshipType = '';
      }
      return next;
    });
  };
  const submit = () => {
    const payload: SubProfileFormData = {
      ...formData,
      name: formData.name && formData.name.trim() !== '' ? formData.name : `${formData.firstName} ${formData.lastName}`.trim(),
      // Leere Zeilen aus dem Editor gehören nicht in den Datensatz.
      communications: (formData.communications || []).filter((weg) => String(weg.value || '').trim() !== ''),
    };
    onSave(payload);
  };
  const canPrimary = (formData.firstName || '').trim() !== '' && (formData.lastName || '').trim() !== '';

  return (
    <WizardShell
      open={isOpen}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={initialData ? 'Schülerprofil bearbeiten' : 'Neues Schülerprofil hinzufügen'}
      description={
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs">ID: {parent.id}</span>
            {parent.wpUserId ? <span className="font-mono text-xs">WP: {parent.wpUserId}</span> : null}
          </div>
          <div>Für {parent.name}</div>
        </div>
      }
      contentClassName="w-[1080px] h-[930px] max-w-[95vw] max-h-[95vh]"
      onCancel={onClose}
      cancelLabel="Abbrechen"
      onPrimary={submit}
      primaryLabel={initialData ? 'Speichern' : 'Schülerprofil erstellen'}
      canPrimary={canPrimary}
    >
      <div className="py-4 space-y-4">
        <div>
          <label htmlFor="salutation" className="text-sm font-medium">Anrede</label>
          <Select value={formData.salutation || ''} onValueChange={value => handleSelectChange('salutation', value)}>
            <SelectTrigger id="salutation" className="mt-1">
              <SelectValue placeholder="Anrede auswählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Herr">Herr</SelectItem>
              <SelectItem value="Frau">Frau</SelectItem>
              <SelectItem value="Divers">Divers</SelectItem>
              <SelectItem value="Firma">Firma</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label htmlFor="relationshipType" className="text-sm font-medium">Beziehung zur Hauptperson</label>
          <Select value={formData.relationshipType || ''} onValueChange={value => handleSelectChange('relationshipType', value)}>
            <SelectTrigger id="relationshipType" className="mt-1">
              <SelectValue placeholder="Beziehung auswählen" />
            </SelectTrigger>
            <SelectContent>
              {relationshipOptionsFor(formData.salutation).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="text-sm font-medium">Vorname</label>
            <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} className="mt-1" required />
          </div>
          <div>
            <label htmlFor="lastName" className="text-sm font-medium">Nachname</label>
            <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} className="mt-1" required />
          </div>
        </div>

        <div>
          <label htmlFor="name" className="text-sm font-medium">Anzeigename (optional)</label>
          <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Falls leer: wird aus Vor- und Nachname gebildet" className="mt-1" />
        </div>

        <div>
          <label htmlFor="birthDate" className="text-sm font-medium">Geburtsdatum</label>
          <Input id="birthDate" name="birthDate" type="date" value={formData.birthDate} onChange={handleInputChange} className="mt-1" />
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Kontaktwege</div>
          <p className="text-xs text-muted-foreground">
            Eigene E-Mail-Adressen und Nummern des Profils. Nachrichten gehen zusätzlich an den Kunden; ohne eigene
            Kontaktwege erreicht das Büro das Profil über das Kundenkonto.
          </p>
          {/* Dieselbe Liste wie in der Akte — vorher fehlte hier die Art „E-Mail" ganz (T-0289). */}
          <KontaktwegeEditor
            idPrefix="subprofil"
            wege={formData.communications || []}
            onChange={(wege) => setFormData((prev) => ({ ...prev, communications: wege }))}
          />
        </div>
      </div>
    </WizardShell>
  );
};
export default SubProfileForm;